import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, Wallet, AlertCircle, AlertTriangle,
  ArrowUpRight, ArrowDownRight, Calendar, ArrowRight, CheckCircle2, ChevronRight
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/format';
import FinancialHeader from './FinancialHeader';
import NewTransactionModal from './NewTransactionModal';
import SettleModal from './SettleModal';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

const PIE_COLORS = ['#C52429', '#2563EB', '#F59E0B', '#10B981', '#8B5CF6', '#EC4899', '#06B6D4', '#64748B'];

function KpiCard({ title, value, subValue, icon: Icon, tone = 'default', testid }) {
  const toneClasses = {
    default: 'text-neutral-900 dark:text-neutral-100 bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400',
    success: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400',
    danger: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400',
    warning: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400',
    brand: 'text-brand bg-brand/10 text-brand',
    info: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400',
  };

  const currentTone = toneClasses[tone] || toneClasses.default;
  const [valColor, ...badgeStyles] = currentTone.split(' ');
  const badgeStyle = badgeStyles.join(' ');

  return (
    <div data-testid={testid} className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5 shadow-xs">
      <div className="flex items-start justify-between mb-2">
        <p className="text-xs uppercase font-semibold tracking-wider text-neutral-500 dark:text-neutral-400">{title}</p>
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${badgeStyle}`}>
          <Icon className="h-4.5 w-4.5" />
        </div>
      </div>
      <p className={`text-2xl font-bold tracking-tight ${valColor}`}>{value}</p>
      {subValue && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 flex items-center gap-1">
          {subValue}
        </p>
      )}
    </div>
  );
}

export default function FinancialDashboard() {
  const [ownerCompanyId, setOwnerCompanyId] = useState('all');
  const [ownerCompanies, setOwnerCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Modals
  const [newTxModalOpen, setNewTxModalOpen] = useState(false);
  const [settleModalOpen, setSettleModalOpen] = useState(false);
  const [selectedTxForSettle, setSelectedTxForSettle] = useState(null);

  // Upcoming items
  const [upcomingPayables, setUpcomingPayables] = useState([]);
  const [upcomingReceivables, setUpcomingReceivables] = useState([]);

  // Load companies
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/owner-companies');
        setOwnerCompanies(res.data?.data || res.data || []);
      } catch (err) {
        console.error('Erro ao buscar empresas', err);
      }
    })();
  }, []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (ownerCompanyId && ownerCompanyId !== 'all') {
        params.owner_company_id = ownerCompanyId;
      }

      const res = await api.get('/financial/dashboard', { params });
      setData(res.data?.data || res.data || null);

      // Load upcoming 7 days payables and receivables
      const today = new Date().toISOString().substring(0, 10);
      const in7Days = new Date(Date.now() + 7 * 86400000).toISOString().substring(0, 10);

      const [payRes, recRes] = await Promise.all([
        api.get('/financial/transactions', {
          params: {
            ...params,
            type: 'expense',
            payment_status: 'pending',
            start_date: today,
            end_date: in7Days,
            limit: 5,
          }
        }),
        api.get('/financial/transactions', {
          params: {
            ...params,
            type: 'revenue',
            payment_status: 'pending',
            start_date: today,
            end_date: in7Days,
            limit: 5,
          }
        })
      ]);

      const payList = payRes.data?.items || payRes.data?.data || (Array.isArray(payRes.data) ? payRes.data : []);
      const recList = recRes.data?.items || recRes.data?.data || (Array.isArray(recRes.data) ? recRes.data : []);
      setUpcomingPayables(payList);
      setUpcomingReceivables(recList);
    } catch (err) {
      console.error('Erro ao carregar dashboard financeiro', err);
    } finally {
      setLoading(false);
    }
  }, [ownerCompanyId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const kpis = data?.kpis || {};
  const charts = data?.charts || {};

  const handleSettle = (tx) => {
    setSelectedTxForSettle(tx);
    setSettleModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <FinancialHeader
        title="Dashboard Financeiro"
        subtitle="Controle centralizado de receitas, despesas, margens e liquidez"
        ownerCompanyId={ownerCompanyId}
        setOwnerCompanyId={setOwnerCompanyId}
        ownerCompanies={ownerCompanies}
        onNewTransaction={() => setNewTxModalOpen(true)}
        onRefresh={loadDashboard}
      />

      {loading && !data ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-28 rounded-xl shimmer" />
          ))}
        </div>
      ) : (
        <>
          {/* Alertas Críticos de Vencimento se houver */}
          {(kpis.overdue_payables_count > 0 || kpis.overdue_receivables_count > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {kpis.overdue_payables_count > 0 && (
                <div className="flex items-center justify-between p-3.5 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-xl text-red-900 dark:text-red-300 text-sm">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />
                    <div>
                      <p className="font-semibold">{kpis.overdue_payables_count} {kpis.overdue_payables_count === 1 ? 'conta vencida' : 'contas vencidas'} a pagar!</p>
                      <p className="text-xs text-red-700 dark:text-red-400">Total acumulado em atraso: {formatCurrency(kpis.overdue_payables_amount)}</p>
                    </div>
                  </div>
                  <Link to="/financeiro/pagar?status=overdue">
                    <Button size="sm" variant="outline" className="text-xs border-red-300 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/50">
                      Ver Contas
                    </Button>
                  </Link>
                </div>
              )}

              {kpis.overdue_receivables_count > 0 && (
                <div className="flex items-center justify-between p-3.5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-xl text-amber-900 dark:text-amber-300 text-sm">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
                    <div>
                      <p className="font-semibold">{kpis.overdue_receivables_count} faturas a receber em atraso</p>
                      <p className="text-xs text-amber-700 dark:text-amber-400">Inadimplência de clientes: {formatCurrency(kpis.overdue_receivables_amount)}</p>
                    </div>
                  </div>
                  <Link to="/financeiro/receber?status=overdue">
                    <Button size="sm" variant="outline" className="text-xs border-amber-300 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/50">
                      Cobranças
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* KPI Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              title="Receitas Realizadas"
              value={formatCurrency(kpis.revenue_realized)}
              subValue={`Previsto: ${formatCurrency(kpis.revenue_projected)}`}
              icon={ArrowUpRight}
              tone="success"
              testid="kpi-receitas"
            />
            <KpiCard
              title="Despesas Realizadas"
              value={formatCurrency(kpis.expense_realized)}
              subValue={`Previsto: ${formatCurrency(kpis.expense_projected)}`}
              icon={ArrowDownRight}
              tone="danger"
              testid="kpi-despesas"
            />
            <KpiCard
              title="Saldo em Caixa & Bancos"
              value={formatCurrency(kpis.current_balance)}
              subValue="Disponibilidade líquida imediata"
              icon={Wallet}
              tone="info"
              testid="kpi-saldo"
            />
            <KpiCard
              title="Margem Líquida"
              value={`${kpis.margin_percent || 0}%`}
              subValue={`Resultado: ${formatCurrency(kpis.net_realized)}`}
              icon={TrendingUp}
              tone={kpis.margin_percent >= 15 ? 'success' : kpis.margin_percent >= 0 ? 'warning' : 'danger'}
              testid="kpi-margem"
            />
          </div>

          {/* Gráficos Recharts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Gráfico 1: Evolução Mensal (Receitas vs Despesas) */}
            <div className="lg:col-span-2 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">Receitas vs Despesas (Últimos 6 meses)</h3>
                  <p className="text-xs text-neutral-500">Comparativo mensal consolidado de faturamento e custos</p>
                </div>
              </div>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={charts.monthly_evolution || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis dataKey="month" fontSize={11} stroke="#888" />
                    <YAxis fontSize={11} stroke="#888" tickFormatter={v => `R$ ${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={(v) => [formatCurrency(v)]}
                      contentStyle={{ backgroundColor: 'rgba(23, 23, 23, 0.95)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="receitas" name="Receitas" fill="#10B981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="despesas" name="Despesas" fill="#EF4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Gráfico 2: Composição de Despesas por Categoria */}
            <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
              <div className="mb-4">
                <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">Despesas por Categoria</h3>
                <p className="text-xs text-neutral-500">Distribuição percentual dos principais custos</p>
              </div>
              {charts.categories?.length > 0 ? (
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={charts.categories}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={3}
                      >
                        {charts.categories.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => [formatCurrency(v)]} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-72 flex items-center justify-center text-xs text-neutral-400">
                  Nenhuma despesa registrada no período
                </div>
              )}
            </div>
          </div>

          {/* Gráfico 3: Comparativo Multi-Empresas (Se visualização consolidada) */}
          {ownerCompanyId === 'all' && charts.companies?.length > 1 && (
            <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
              <div className="mb-4">
                <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">Comparativo por Empresa / CNPJ</h3>
                <p className="text-xs text-neutral-500">Desempenho financeiro individual de cada empresa do grupo</p>
              </div>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={charts.companies} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis type="number" fontSize={11} stroke="#888" tickFormatter={v => `R$ ${(v / 1000).toFixed(0)}k`} />
                    <YAxis dataKey="name" type="category" fontSize={11} stroke="#888" width={140} />
                    <Tooltip formatter={(v) => [formatCurrency(v)]} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="receitas" name="Receitas" fill="#10B981" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="despesas" name="Despesas" fill="#EF4444" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Próximos Vencimentos (A Pagar & A Receber nos próximos 7 dias) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Próximas Contas a Pagar */}
            <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-md bg-red-100 dark:bg-red-950/50 flex items-center justify-center text-red-600 dark:text-red-400">
                    <ArrowDownRight className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">A Pagar nos Próximos 7 Dias</h3>
                    <p className="text-xs text-neutral-500">Contas e obrigações operacionais imediatas</p>
                  </div>
                </div>
                <Link to="/financeiro/pagar" className="text-xs text-brand hover:underline font-medium flex items-center gap-1">
                  Ver todas <ChevronRight className="h-3 w-3" />
                </Link>
              </div>

              {upcomingPayables.length === 0 ? (
                <div className="py-8 text-center text-xs text-neutral-500">
                  <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto mb-2" />
                  Nenhuma conta a pagar vencendo nos próximos 7 dias!
                </div>
              ) : (
                <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {upcomingPayables.map((tx) => (
                    <div key={tx.id} className="py-2.5 flex items-center justify-between text-xs">
                      <div className="min-w-0 pr-3">
                        <p className="font-medium text-neutral-900 dark:text-neutral-100 truncate">{tx.description}</p>
                        <p className="text-neutral-500 dark:text-neutral-400 truncate">
                          {tx.supplier_name || tx.category_name || 'Despesa'} • Venc: {formatDate(tx.due_date)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-semibold text-neutral-900 dark:text-neutral-100">{formatCurrency(tx.amount)}</span>
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => handleSettle(tx)}
                          className="h-7 text-[11px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 border-red-200 dark:border-red-900"
                        >
                          Dar Baixa
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Próximas Contas a Receber */}
            <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-md bg-emerald-100 dark:bg-emerald-950/50 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">A Receber nos Próximos 7 Dias</h3>
                    <p className="text-xs text-neutral-500">Faturamentos de contratos e clientes previstos</p>
                  </div>
                </div>
                <Link to="/financeiro/receber" className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline font-medium flex items-center gap-1">
                  Ver todas <ChevronRight className="h-3 w-3" />
                </Link>
              </div>

              {upcomingReceivables.length === 0 ? (
                <div className="py-8 text-center text-xs text-neutral-500">
                  <Calendar className="h-6 w-6 text-neutral-400 mx-auto mb-2" />
                  Nenhum recebimento previsto para os próximos 7 dias.
                </div>
              ) : (
                <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {upcomingReceivables.map((tx) => (
                    <div key={tx.id} className="py-2.5 flex items-center justify-between text-xs">
                      <div className="min-w-0 pr-3">
                        <p className="font-medium text-neutral-900 dark:text-neutral-100 truncate">{tx.description}</p>
                        <p className="text-neutral-500 dark:text-neutral-400 truncate">
                          {tx.client_name || 'Cliente'} • Venc: {formatDate(tx.due_date)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">{formatCurrency(tx.amount)}</span>
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => handleSettle(tx)}
                          className="h-7 text-[11px] text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900"
                        >
                          Receber
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      <NewTransactionModal
        open={newTxModalOpen}
        onOpenChange={setNewTxModalOpen}
        onSuccess={loadDashboard}
      />

      <SettleModal
        open={settleModalOpen}
        onOpenChange={setSettleModalOpen}
        transaction={selectedTxForSettle}
        onSuccess={loadDashboard}
      />
    </div>
  );
}
